import React from "react";
import { Link, useLocation } from "react-router-dom";
import { FreemanJonesLogo } from "./FreemanJonesLogo";
import { ExternalLinkIcon } from "./primitives";
import { businessDetails } from "../data/content";
import { track } from "../lib/analytics";

const ext = { target: "_blank", rel: "noopener noreferrer" };

const col1 = [
  { label: "About Us", href: "#about" },
  { label: "Understanding Your IVA", href: "#understanding-iva" },
  { label: "Customer Support", href: "#support-timeline" },
  { label: "Contact Us", href: "#contact" },
];

const legalLinks = [
  { label: "Privacy Policy", href: businessDetails.privacyPolicyUrl, external: true },
  { label: "Cookie Policy", to: "/cookie-policy" },
  { label: "Accessibility", to: "/accessibility" },
  { label: "Complaints Policy", href: businessDetails.complaintsPolicyUrl, external: true },
  { label: "Terms and Conditions", href: "https://www.financialwellnessgroup.co.uk/iva-standard-conditions/", external: true },
];

export const SiteFooter = () => {
  const { pathname } = useLocation();
  // Section anchors need a "/" prefix when viewing a standalone legal page.
  const home = pathname === "/" ? "" : "/";

  return (
    <footer className="bg-brand-navy text-white" data-testid="site-footer">
      <div className="mx-auto max-w-content px-4 py-14 lg:px-8">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <FreemanJonesLogo variant="light" />
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-white/70">
              Supporting people throughout their Individual Voluntary Arrangement, from first
              proposal right through to completion.
            </p>
          </div>

          <FooterCol title="Freeman Jones" links={col1.map((l) => ({ ...l, href: home + l.href }))} />

          <div>
            <h3 className="font-heading text-sm font-bold uppercase tracking-[0.14em] text-white/60">
              Customer Services
            </h3>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li>
                <a {...ext} href={businessDetails.customerAppUrl}
                  onClick={() => track("customer_app_clicked", { location: "footer" })}
                  className="inline-flex items-center gap-1.5 text-white/80 hover:text-white" data-testid="footer-app">
                  Customer App <ExternalLinkIcon className="h-3.5 w-3.5" />
                </a>
              </li>
              <li>
                <a {...ext} href={businessDetails.digitalPostboxUrl}
                  onClick={() => track("digital_postbox_clicked", { location: "footer" })}
                  className="inline-flex items-center gap-1.5 text-white/80 hover:text-white" data-testid="footer-postbox">
                  Digital Postbox <ExternalLinkIcon className="h-3.5 w-3.5" />
                </a>
              </li>
              <li><a href={`${home}#documents`} className="text-white/80 hover:text-white">Important Documents</a></li>
              <li><a href={`${home}#faqs`} className="text-white/80 hover:text-white">FAQs</a></li>
              <li><a href={`${home}#complaints`} className="text-white/80 hover:text-white">Complaints</a></li>
            </ul>
          </div>

          <div>
            <h3 className="font-heading text-sm font-bold uppercase tracking-[0.14em] text-white/60">Contact</h3>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li>
                <a href={businessDetails.phoneLink} onClick={() => track("phone_number_clicked", { location: "footer" })} className="text-white/80 hover:text-white">
                  {businessDetails.phoneDisplay}
                </a>
              </li>
              <li>
                <a href={businessDetails.emailLink} onClick={() => track("email_address_clicked", { location: "footer" })} className="break-all text-white/80 hover:text-white">
                  {businessDetails.email}
                </a>
              </li>
              <li className="text-white/70">{businessDetails.registeredOffice}</li>
            </ul>
            <h3 className="mt-6 font-heading text-sm font-bold uppercase tracking-[0.14em] text-white/60">Legal</h3>
            <ul className="mt-3 space-y-2 text-sm">
              {legalLinks.map((l) => (
                <li key={l.label}>
                  {l.external ? (
                    <a {...ext} href={l.href} className="text-white/80 hover:text-white">{l.label}</a>
                  ) : (
                    <Link to={l.to} className="text-white/80 hover:text-white" data-testid={`footer${l.to.replace(/\//g, "-")}`}>{l.label}</Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 space-y-2 border-t border-white/10 pt-8 text-xs leading-relaxed text-white/55">
          <p>Financial Wellness Group is a trading style of Freeman Jones Limited.</p>
          <p>
            Freeman Jones Limited is registered in England and Wales. Company registration number:{" "}
            {businessDetails.companyNumber}.
          </p>
          <p>Registered office: {businessDetails.registeredOffice}.</p>
          <p>
            Freeman Jones Limited has Licensed Insolvency Practitioners who are authorised by the
            Insolvency Practitioners Association.
          </p>
          <p>
            Catherine McNeill &amp; Michael Bellingham are licenced Insolvency Practitioners
            regulated by The Insolvency Practitioners Association.
          </p>
          <p>Calls may be recorded for training and quality purposes.</p>
          <p>
            Any advice provided is general information given in contemplation of an insolvency
            appointment and is not personal financial or legal advice.
          </p>
          <p className="pt-2 text-white/40">
            © {new Date().getFullYear()} Freeman Jones Limited. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

const FooterCol = ({ title, links }) => (
  <div>
    <h3 className="font-heading text-sm font-bold uppercase tracking-[0.14em] text-white/60">{title}</h3>
    <ul className="mt-4 space-y-2.5 text-sm">
      {links.map((l) => (
        <li key={l.label}>
          <a href={l.href} className="text-white/80 hover:text-white">{l.label}</a>
        </li>
      ))}
    </ul>
  </div>
);
